import React, { useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { CheckCircle } from "@phosphor-icons/react";
import { splitMatterLabel } from "../../../utils/matterLabel";
import { status } from "../../../theme/tokens";
import {
  FoldToggleButton,
  MatterCard,
  MatterCardHeader,
  RowBody,
  RowMeta,
  RowShell,
  RowTitleButton,
  RowTop,
  RowVerbButton,
  StateLine,
} from "./MyWorkStyled";

const SHOWN_AT_FIRST = 5;

/**
 * Work the reader asked for that someone else finished and closed this
 * past week, with the note they closed it with (Matthew, 9/29: "I see
 * that back in my inbox just for the warm and fuzzy that it was done").
 * The title opens the matter; Acknowledge takes the row off the
 * reader's card (Matthew, 10/1). Unacknowledged rows still age out
 * org-side after seven days. Someone else's view is read-only.
 */
const FinishedStrip = ({ rows, onOpen, onAcknowledge, readOnly }) => {
  const [showAll, setShowAll] = useState(false);
  if (!rows || rows.length === 0) return null;
  const visible = showAll ? rows : rows.slice(0, SHOWN_AT_FIRST);

  return (
    <MatterCard data-testid="finished-strip" style={{ marginBottom: 12 }}>
      <MatterCardHeader>
        <CheckCircle
          size={14}
          weight="fill"
          style={{ verticalAlign: "-2px", marginRight: 6, color: status.ready.fg }}
        />
        Finished this week
      </MatterCardHeader>
      {visible.map((row) => {
        const matter = splitMatterLabel(row.matter_name);
        return (
          <RowShell key={row.key} data-testid={`finished-row-${row.key}`}>
            <RowTop>
              <RowBody>
                <RowTitleButton
                  type="button"
                  title={row.title || ""}
                  onClick={() => onOpen(row)}
                >
                  {row.title || "Untitled"}
                </RowTitleButton>
                <RowMeta>
                  {matter.short && (
                    <span title={matter.full}>{matter.short}</span>
                  )}
                  {row.closed_by && <span>closed by {row.closed_by}</span>}
                  {row.closed_at && (
                    <span>{dayjs(row.closed_at).format("M/D")}</span>
                  )}
                </RowMeta>
                {row.note && <StateLine>“{row.note}”</StateLine>}
              </RowBody>
              {!readOnly && onAcknowledge && (
                <RowVerbButton
                  type="button"
                  onClick={() => onAcknowledge(row)}
                >
                  Acknowledge
                </RowVerbButton>
              )}
            </RowTop>
          </RowShell>
        );
      })}
      {rows.length > SHOWN_AT_FIRST && (
        <FoldToggleButton
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          aria-expanded={showAll}
        >
          {showAll ? "Show fewer" : "Show the rest"}
        </FoldToggleButton>
      )}
    </MatterCard>
  );
};

FinishedStrip.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  onOpen: PropTypes.func.isRequired,
  onAcknowledge: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default FinishedStrip;
